/**
 * LGM — SIDE A / SIDE B gallery toggle
 * ---------------------------------------------------------------------------
 * Reversible garments carry both faces in ONE product gallery (sides are not a
 * variant option — see CUSTOMIZATIONS.md "Session 17"). This filters the
 * gallery down to one face at a time.
 *
 * It does NOT hide DOM by hand. Prestige's <scroll-carousel> exposes
 * `filter(indexes)`, which sets `hidden` on the cells AND emits
 * `carousel:filter`; <product-gallery-navigation> (the thumbnail filmstrip)
 * extends <carousel-navigation>, which listens for that event and hides the
 * matching thumbnails. So one call keeps photos, filmstrip and dots in sync.
 *
 * Two things this has to survive, both verified in assets/theme.js:
 *
 * 1. A SIZE CHANGE UN-FILTERS THE GALLERY. `product:rerender` reaches
 *    ProductGallery#onSectionRerender, which compares `galleryMarkup
 *    .filteredIndex` (a property that does not exist — Prestige typo) against
 *    `this.filteredIndexes` (an array). Never equal, so it always re-filters,
 *    with a value parsed off an element that may not be upgraded — i.e.
 *    `undefined`, which `filter()` defaults to `[]`. Everything reappears.
 *    Hence the `carousel:filter` listener below, which re-applies our side
 *    whenever something else clears it.
 *
 * 2. THIS ELEMENT IS DESTROYED AND REBUILT on a size change. The toggle is a
 *    `liquid` block, and `liquid` IS in ProductRerender's partial-rerender
 *    block list, so the element is replaced wholesale. Instance state would be
 *    lost, so the chosen side is parked on the <product-gallery> element
 *    (`dataset.lgmSide`), which is never replaced.
 */

class LgmSideToggle extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();

    try {
      this.sides = {
        a: JSON.parse(this.dataset.sideA || '[]'),
        b: JSON.parse(this.dataset.sideB || '[]'),
      };
    } catch (error) {
      return;
    }

    this.gallery = this.closest('.shopify-section')?.querySelector('product-gallery');

    if (!this.gallery?.carousel || !this.sides.a.length || !this.sides.b.length) {
      return;
    }

    this.buttons = Array.from(this.querySelectorAll('[data-side]'));
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.select(button.dataset.side), {
        signal: this.abortController.signal,
      });
    });

    /* Re-apply whenever anything else clears the filter — see note 1 above.
       The listener lives on the carousel, which outlives this element, so it
       is bound to an AbortController and dropped on disconnect. */
    this.gallery.carousel.addEventListener(
      'carousel:filter',
      () => {
        if (this.isApplying) return;
        this.apply(this.currentSide);
      },
      { signal: this.abortController.signal }
    );

    /* Restore the side that was showing before this element was rebuilt. */
    this.select(this.currentSide, { scroll: false });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get currentSide() {
    return this.gallery?.dataset.lgmSide === 'b' ? 'b' : 'a';
  }

  select(side, { scroll = true } = {}) {
    this.gallery.dataset.lgmSide = side;
    this.apply(side);

    this.buttons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.side === side));
    });

    /* Land on the first photo of the face just chosen. Only on a scrollable
       carousel (mobile) — on desktop the grid re-flows in place and Prestige's
       `select()` indexes into VISIBLE cells, so 0 is that face's opening shot. */
    if (scroll && this.gallery.carousel.isScrollable) {
      this.gallery.carousel.select(0, { instant: true });
    }
  }

  apply(side) {
    const hidden = side === 'b' ? this.sides.a : this.sides.b;

    /* Union with whatever Prestige is already filtering, so this never
       un-hides a media the variant-images hack meant to hide. The getter is
       0-based; it throws if the attribute is missing. */
    let inherited = [];
    try {
      inherited = this.gallery.filteredIndexes || [];
    } catch (error) {
      inherited = [];
    }

    const indexes = [...new Set([...inherited, ...hidden])].sort((a, b) => a - b);

    this.isApplying = true;
    this.gallery.carousel.filter(indexes);
    this.isApplying = false;
  }
}

if (!window.customElements.get('lgm-side-toggle')) {
  window.customElements.define('lgm-side-toggle', LgmSideToggle);
}
